import React, { useState } from 'react';
import { Plus, Pencil, Trash2, X, Check, Link } from 'lucide-react';
import { Challenge, Category, Difficulty } from '../types';
import { useChallenges } from '../context/ChallengeContext';

const categories: Category[] = ['Composition', 'Lighting', 'Technical', 'Creative'];
const difficulties: Difficulty[] = ['Beginner', 'Intermediate', 'Advanced'];

interface ChallengeFormData {
  title: string;
  description: string;
  category: Category;
  difficulty: Difficulty;
  technicalGuidance: string[];
  creativeApproach: string[];
  inspiration: { url: string; description: string; }[];
  featuredIn: { title: string; source: string; }[];
}

const emptyChallengeForm: ChallengeFormData = {
  title: '',
  description: '',
  category: 'Composition',
  difficulty: 'Beginner',
  technicalGuidance: [''],
  creativeApproach: [''],
  inspiration: [{ url: '', description: '' }],
  featuredIn: []
};

const ChallengeManager: React.FC = () => {
  const { challenges, addChallenge, updateChallenge, deleteChallenge, loading, error } = useChallenges();
  const [isEditing, setIsEditing] = useState(false);
  const [editingChallenge, setEditingChallenge] = useState<ChallengeFormData>(emptyChallengeForm);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const handleEdit = (challenge: Challenge) => {
    setEditingChallenge({
      title: challenge.title,
      description: challenge.description,
      category: challenge.category,
      difficulty: challenge.difficulty,
      technicalGuidance: [...challenge.technicalGuidance],
      creativeApproach: [...challenge.creativeApproach],
      inspiration: challenge.inspiration ? [...challenge.inspiration] : [],
      featuredIn: challenge.featuredIn ? [...challenge.featuredIn] : []
    });
    setEditingId(challenge.id);
    setIsEditing(true);
  };

  const handleAdd = () => {
    setEditingChallenge(emptyChallengeForm);
    setEditingId(null);
    setIsEditing(true);
  };

  const handleDelete = async (id: string) => {
    if (confirm('Are you sure you want to delete this challenge?')) {
      try {
        await deleteChallenge(id);
      } catch (err) {
        alert('Failed to delete challenge');
      }
    }
  };

  const handleSave = async () => {
    try {
      setSaveError(null);
      if (editingId) {
        await updateChallenge(editingId, { ...editingChallenge, id: editingId });
      } else {
        await addChallenge(editingChallenge);
      }
      setIsEditing(false);
      setEditingId(null);
    } catch (err) {
      setSaveError('Failed to save challenge');
    }
  };

  const handleArrayFieldAdd = (field: 'technicalGuidance' | 'creativeApproach' | 'inspiration' | 'featuredIn') => {
    if (field === 'inspiration') {
      setEditingChallenge({
        ...editingChallenge,
        inspiration: [...editingChallenge.inspiration, { url: '', description: '' }]
      });
    } else if (field === 'featuredIn') {
      setEditingChallenge({
        ...editingChallenge,
        featuredIn: [...editingChallenge.featuredIn, { title: '', source: '' }]
      });
    } else {
      setEditingChallenge({
        ...editingChallenge,
        [field]: [...editingChallenge[field], '']
      });
    }
  };

  const handleArrayFieldRemove = (field: 'technicalGuidance' | 'creativeApproach' | 'inspiration' | 'featuredIn', index: number) => {
    setEditingChallenge({
      ...editingChallenge,
      [field]: editingChallenge[field].filter((_, i) => i !== index)
    });
  };

  if (loading) {
    return <div className="text-center py-10">Loading challenges...</div>;
  }

  if (error) {
    return <div className="text-center py-10 text-red-500">Error: {error}</div>;
  }

  return (
    <div className="p-6 bg-[#121212]">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-white">Challenge Manager</h1>
        <button
          onClick={handleAdd}
          className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2"
        >
          <Plus size={20} />
          Add Challenge
        </button>
      </div>

      {saveError && (
        <div className="bg-red-500/10 border border-red-500/50 text-red-400 px-4 py-2 rounded mb-4">
          {saveError}
        </div>
      )}

      {isEditing ? (
        <div className="bg-[#1e1e1e] rounded-lg shadow p-6 border border-[#333333] space-y-6">
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Title</label>
              <input
                type="text"
                value={editingChallenge.title}
                onChange={e => setEditingChallenge({ ...editingChallenge, title: e.target.value })}
                className="w-full bg-[#2a2a2a] border border-[#333333] rounded-lg px-4 py-2 text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Category</label>
              <select
                value={editingChallenge.category}
                onChange={e => setEditingChallenge({ ...editingChallenge, category: e.target.value as Category })}
                className="w-full bg-[#2a2a2a] border border-[#333333] rounded-lg px-4 py-2 text-white"
              >
                {categories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
            <textarea
              value={editingChallenge.description}
              onChange={e => setEditingChallenge({ ...editingChallenge, description: e.target.value })}
              className="w-full bg-[#2a2a2a] border border-[#333333] rounded-lg px-4 py-2 text-white h-32"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Difficulty</label>
            <select
              value={editingChallenge.difficulty}
              onChange={e => setEditingChallenge({ ...editingChallenge, difficulty: e.target.value as Difficulty })}
              className="w-full bg-[#2a2a2a] border border-[#333333] rounded-lg px-4 py-2 text-white"
            >
              {difficulties.map(difficulty => (
                <option key={difficulty} value={difficulty}>{difficulty}</option>
              ))}
            </select>
          </div>

          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="text-sm font-medium text-gray-300">Technical Guidance</label>
              <button
                onClick={() => handleArrayFieldAdd('technicalGuidance')}
                className="text-blue-400 hover:text-blue-300"
              >
                <Plus size={20} />
              </button>
            </div>
            {editingChallenge.technicalGuidance.map((guidance, index) => (
              <div key={index} className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={guidance}
                  onChange={e => {
                    const newGuidance = [...editingChallenge.technicalGuidance];
                    newGuidance[index] = e.target.value;
                    setEditingChallenge({ ...editingChallenge, technicalGuidance: newGuidance });
                  }}
                  className="flex-1 bg-[#2a2a2a] border border-[#333333] rounded-lg px-4 py-2 text-white"
                />
                <button
                  onClick={() => handleArrayFieldRemove('technicalGuidance', index)}
                  className="text-red-400 hover:text-red-300"
                >
                  <X size={20} />
                </button>
              </div>
            ))}
          </div>

          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="text-sm font-medium text-gray-300">Creative Approach</label>
              <button
                onClick={() => handleArrayFieldAdd('creativeApproach')}
                className="text-blue-400 hover:text-blue-300"
              >
                <Plus size={20} />
              </button>
            </div>
            {editingChallenge.creativeApproach.map((approach, index) => (
              <div key={index} className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={approach}
                  onChange={e => {
                    const newApproach = [...editingChallenge.creativeApproach];
                    newApproach[index] = e.target.value;
                    setEditingChallenge({ ...editingChallenge, creativeApproach: newApproach });
                  }}
                  className="flex-1 bg-[#2a2a2a] border border-[#333333] rounded-lg px-4 py-2 text-white"
                />
                <button
                  onClick={() => handleArrayFieldRemove('creativeApproach', index)}
                  className="text-red-400 hover:text-red-300"
                >
                  <X size={20} />
                </button>
              </div>
            ))}
          </div>

          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="text-sm font-medium text-gray-300">Inspiration Images</label>
              <button
                onClick={() => handleArrayFieldAdd('inspiration')}
                className="text-blue-400 hover:text-blue-300"
              >
                <Plus size={20} />
              </button>
            </div>
            {editingChallenge.inspiration.map((image, index) => (
              <div key={index} className="grid grid-cols-2 gap-2 mb-2">
                <input
                  type="text"
                  placeholder="Image URL"
                  value={image.url}
                  onChange={e => {
                    const newImages = [...editingChallenge.inspiration];
                    newImages[index] = { ...image, url: e.target.value };
                    setEditingChallenge({ ...editingChallenge, inspiration: newImages });
                  }}
                  className="bg-[#2a2a2a] border border-[#333333] rounded-lg px-4 py-2 text-white"
                />
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Description"
                    value={image.description}
                    onChange={e => {
                      const newImages = [...editingChallenge.inspiration];
                      newImages[index] = { ...image, description: e.target.value };
                      setEditingChallenge({ ...editingChallenge, inspiration: newImages });
                    }}
                    className="flex-1 bg-[#2a2a2a] border border-[#333333] rounded-lg px-4 py-2 text-white"
                  />
                  <button
                    onClick={() => handleArrayFieldRemove('inspiration', index)}
                    className="text-red-400 hover:text-red-300"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="text-sm font-medium text-gray-300">Featured In</label>
              <button
                onClick={() => handleArrayFieldAdd('featuredIn')}
                className="text-blue-400 hover:text-blue-300"
              >
                <Plus size={20} />
              </button>
            </div>
            {editingChallenge.featuredIn.map((feature, index) => (
              <div key={index} className="grid grid-cols-2 gap-2 mb-2">
                <input
                  type="text"
                  placeholder="Title"
                  value={feature.title}
                  onChange={e => {
                    const newFeatured = [...editingChallenge.featuredIn];
                    newFeatured[index] = { ...feature, title: e.target.value };
                    setEditingChallenge({ ...editingChallenge, featuredIn: newFeatured });
                  }}
                  className="bg-[#2a2a2a] border border-[#333333] rounded-lg px-4 py-2 text-white"
                />
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Source URL"
                    value={feature.source}
                    onChange={e => {
                      const newFeatured = [...editingChallenge.featuredIn];
                      newFeatured[index] = { ...feature, source: e.target.value };
                      setEditingChallenge({ ...editingChallenge, featuredIn: newFeatured });
                    }}
                    className="flex-1 bg-[#2a2a2a] border border-[#333333] rounded-lg px-4 py-2 text-white"
                  />
                  <button
                    onClick={() => handleArrayFieldRemove('featuredIn', index)}
                    className="text-red-400 hover:text-red-300"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-end gap-4">
            <button
              onClick={() => setIsEditing(false)}
              className="px-4 py-2 text-gray-300 hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg flex items-center gap-2"
            >
              <Check size={20} />
              Save
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-[#1e1e1e] rounded-lg shadow overflow-hidden border border-[#333333]">
          <table className="w-full">
            <thead>
              <tr className="bg-[#2a2a2a]">
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-300">Title</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-300">Category</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-300">Difficulty</th>
                <th className="px-6 py-3 text-right text-sm font-medium text-gray-300">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#333333]">
              {challenges.map(challenge => (
                <tr key={challenge.id} className="hover:bg-[#2a2a2a]">
                  <td className="px-6 py-4 text-sm text-gray-300">{challenge.title}</td>
                  <td className="px-6 py-4 text-sm text-gray-300">{challenge.category}</td>
                  <td className="px-6 py-4 text-sm text-gray-300">{challenge.difficulty}</td>
                  <td className="px-6 py-4 text-right space-x-2">
                    <button
                      onClick={() => handleEdit(challenge)}
                      className="text-blue-400 hover:text-blue-300 transition-colors"
                    >
                      <Pencil size={20} />
                    </button>
                    <button
                      onClick={() => handleDelete(challenge.id)}
                      className="text-red-400 hover:text-red-300 transition-colors"
                    >
                      <Trash2 size={20} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ChallengeManager;